import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, alpha, useTheme } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { gameIconSx } from '../../helpers/styles'
import { useCrownJewelsRows } from '../../hooks/useCrownJewelsRows'
import { GameCreativeDetailsData, GameMetadata } from '../../types'
import { CrownJewelsRowExpander, TOP_K } from '../CrownJewelsRowExpander/CrownJewelsRowExpander'
import { SectionCard } from '../SectionCard/SectionCard'

interface CrownJewelsTableProps {
  source: GameCreativeDetailsData
  target: GameCreativeDetailsData
  /** Scope-wide leader install share anchor (see `CreativeTagsByGroupSection`). */
  scopeMaxInstallShare: number
}

/**
 * Stage 2 of the redesigned Creative Tags tab: a unified comparison table
 * surfacing each game's "representative best-performing tag set" — the
 * crown jewels we could mine to reproduce the games' winning creative
 * patterns.
 *
 * Layout: a single 3-column table with one row per tag group. Headliner
 * groups render first; execution-detail groups live behind a single
 * bottom-of-table "Show execution detail" toggle so the headline view stays
 * scannable. Unknown / new dimensions are appended after that for forward
 * compatibility — they never silently disappear.
 *
 * Each game's column is colour-coded (source = `secondary.main`,
 * target = `primary.main`) so the table visually rhymes with the radar
 * chart and the per-group diagnosis above. Per-row mechanics
 * (top-{TOP_K} chips + collapse panel + chip rendering) live in
 * {@link CrownJewelsRowExpander}; data ranking lives in
 * {@link useCrownJewelsRows}.
 */
export const CrownJewelsTable: FC<CrownJewelsTableProps> = ({ source, target, scopeMaxInstallShare }) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()

  const { sourceRanked, targetRanked, headlinerGroups, executionGroups, otherGroups, hasAnyContent } = useCrownJewelsRows(source, target, scopeMaxInstallShare)

  const sourceColor = theme.palette.secondary.main
  const targetColor = theme.palette.primary.main

  const [executionExpanded, setExecutionExpanded] = useState(false)

  if (!hasAnyContent) {
    return (
      <SectionCard title={t('compare_crown_jewels_title')}>
        <Typography variant="body2" color="text.secondary">
          {t('compare_tag_section_below_floor')}
        </Typography>
      </SectionCard>
    )
  }

  const executionToggleLabel = executionExpanded
    ? t('compare_tag_hide_execution_detail')
    : t('compare_tag_show_execution_detail', { count: executionGroups.length })

  const renderRow = (dimension: string) => (
    <CrownJewelsRowExpander
      key={dimension}
      dimension={dimension}
      sourceRows={sourceRanked.get(dimension) ?? []}
      targetRows={targetRanked.get(dimension) ?? []}
      sourceGame={source.game}
      targetGame={target.game}
      sourceColor={sourceColor}
      targetColor={targetColor}
    />
  )

  return (
    <SectionCard title={t('compare_crown_jewels_title')}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {t('compare_crown_jewels_caption', { topK: TOP_K })}
        </Typography>
        <TableContainer
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: borderRadiusDefault,
            // Outline doubles as the bottom-row separator strategy.
            backgroundColor: 'background.paper',
          }}
        >
          <Table
            size="small"
            sx={{
              // `tableLayout: 'fixed'` locks column widths to the
              // explicit `width` hints on the header cells regardless of
              // body cell content.
              tableLayout: 'fixed',
              '& td, & th': { borderColor: 'divider' },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha(theme.palette.text.primary, 0.02) }}>
                <TableCell sx={{ fontWeight: 700, width: { xs: 120, md: 180 } }}>{t('compare_crown_jewels_col_group')}</TableCell>
                <GameHeaderCell game={source.game} accent={sourceColor} />
                <GameHeaderCell game={target.game} accent={targetColor} />
                {/* Trailing chevron column has no header label — kept narrow so the row content gets the space. */}
                <TableCell sx={{ width: 48 }} aria-hidden />
              </TableRow>
            </TableHead>
            <TableBody>
              {headlinerGroups.map(renderRow)}
              {executionExpanded && executionGroups.map(renderRow)}
              {otherGroups.map(renderRow)}
            </TableBody>
          </Table>
        </TableContainer>

        {executionGroups.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setExecutionExpanded((prev) => !prev)}
              startIcon={executionExpanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
              aria-expanded={executionExpanded}
              sx={{ fontWeight: 'bold' }}
            >
              {executionToggleLabel}
            </Button>
          </Box>
        )}
      </Box>
    </SectionCard>
  )
}

interface GameHeaderCellProps {
  game: GameMetadata
  accent: string
}

/**
 * Per-game column header cell: the game icon, name (in the game's accent
 * colour), and a "Top {TOP_K} tags per group" subtitle so the cell content
 * below has clear semantics without re-explaining itself row-by-row.
 */
const GameHeaderCell: FC<GameHeaderCellProps> = ({ game, accent }) => {
  const { t } = useTranslation('ua-insights')
  return (
    <TableCell>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {game.icon && <Box component="img" src={game.icon} alt="" sx={{ ...gameIconSx(24), border: '1px solid', borderColor: alpha(accent, 0.3) }} />}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: accent, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {game.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {t('compare_crown_jewels_panel_subtitle', { topK: TOP_K })}
          </Typography>
        </Box>
      </Box>
    </TableCell>
  )
}
